import React, { useState, useEffect } from "react";
import { auth, database } from "../config/firebaseConfig";
import { signOut } from "firebase/auth";
import { ref, onValue, set } from "firebase/database";
import Swal from "sweetalert2";
import { toast } from "react-hot-toast";

const Settings = () => {
    const user = auth.currentUser;
    const [captchaConfig, setCaptchaConfig] = useState({ siteKey: "", secretKey: "" });
    const [githubConfig, setGithubConfig] = useState({
        token: process.env.REACT_APP_GITHUB_TOKEN || "",
        owner: "niteshsrivastava7959",
        repo: "vivaak-frontend"
    });
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingGithub, setIsSavingGithub] = useState(false);
    const [isSavingFonts, setIsSavingFonts] = useState(false);

    // Font Configuration state
    const [fontConfig, setFontConfig] = useState({
        heading: { family: "Cinzel", weight: "400", spacing: "-1px", transform: "uppercase", style: "normal" },
        menu: { family: "Cinzel", weight: "400", spacing: "0.05em", transform: "uppercase", style: "normal" },
        body: { family: "Outfit", weight: "400", spacing: "normal", transform: "none", style: "normal" }
    });

    // Settings Navigation Tab state
    const [activeMainTab, setActiveMainTab] = useState("general"); // "general", "deploy" or "fonts"

    // Build trigger history state
    const [history, setHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [localLogs, setLocalLogs] = useState([]);
    const [activeHistoryTab, setActiveHistoryTab] = useState("audit");
    const [historyError, setHistoryError] = useState("");

    useEffect(() => {
        // Load captcha config
        const captchaRef = ref(database, "config/captcha");
        const unsubscribeCaptcha = onValue(captchaRef, (snap) => {
            const data = snap.val();
            if (data) {
                setCaptchaConfig({
                    siteKey: data.siteKey || "",
                    secretKey: data.secretKey || "",
                });
            }
        });

        // Load github config
        const githubRef = ref(database, "config/github");
        const unsubscribeGithub = onValue(githubRef, (snap) => {
            const data = snap.val();
            if (data) {
                setGithubConfig({
                    token: data.token || process.env.REACT_APP_GITHUB_TOKEN || "",
                    owner: data.owner || "niteshsrivastava7959",
                    repo: data.repo || "vivaak-frontend",
                });
            }
        });

        // Load local deployment audit logs from Firebase database
        const logsRef = ref(database, "config/deployLogs");
        const unsubscribeLogs = onValue(logsRef, (snap) => {
            const data = snap.val();
            if (data) {
                // Convert object to array and sort descending by timestamp
                const sortedLogs = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                })).sort((a, b) => b.timestamp - a.timestamp);
                setLocalLogs(sortedLogs);
            } else {
                setLocalLogs([]);
            }
        });

        // Load Font Settings config
        const fontRef = ref(database, "settings/fontConfig");
        const unsubscribeFont = onValue(fontRef, (snap) => {
            const data = snap.val();
            if (data) {
                setFontConfig(prev => ({
                    heading: { ...prev.heading, ...data.heading },
                    menu: { ...prev.menu, ...data.menu },
                    body: { ...prev.body, ...data.body }
                }));
            }
        });

        return () => {
            unsubscribeCaptcha();
            unsubscribeGithub();
            unsubscribeLogs();
            unsubscribeFont();
        };
    }, []);

    // Fetch build/deployment runs from GitHub Actions API
    const fetchHistory = async () => {
        if (!githubConfig.token || !githubConfig.owner || !githubConfig.repo) return;
        setIsLoadingHistory(true);
        setHistoryError("");
        try {
            const response = await fetch(
                `https://api.github.com/repos/${githubConfig.owner}/${githubConfig.repo}/actions/runs?per_page=7`,
                {
                    headers: {
                        Authorization: `Bearer ${githubConfig.token}`,
                        Accept: "application/vnd.github.v3+json",
                    }
                }
            );
            if (response.ok) {
                const data = await response.json();
                setHistory(data.workflow_runs || []);
                setHistoryError("");
            } else if (response.status === 403 || response.status === 401) {
                setHistoryError("GitHub PAT has restricted permissions. Please make sure the token has 'actions:read' scope to view build runs.");
            } else {
                setHistoryError("Unable to fetch build runs from GitHub. Please check configuration/token.");
            }
        } catch (error) {
            console.error("Error fetching deployment history:", error);
            setHistoryError("Network error. Unable to load GitHub status.");
        } finally {
            setIsLoadingHistory(false);
        }
    };

    // Auto-fetch deployment history when GitHub configuration is loaded/available
    useEffect(() => {
        if (githubConfig.token && githubConfig.owner && githubConfig.repo) {
            fetchHistory();
        }
    }, [githubConfig.token, githubConfig.owner, githubConfig.repo]);

    const handleSaveCaptcha = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await set(ref(database, "config/captcha"), captchaConfig);
            toast.success("reCAPTCHA configuration updated successfully");
        } catch (err) {
            console.error("Error saving captcha config:", err);
            toast.error("Failed to update reCAPTCHA configuration");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveFonts = async (e) => {
        e.preventDefault();
        setIsSavingFonts(true);
        try {
            await set(ref(database, "settings/fontConfig"), fontConfig);
            toast.success("Typography Settings saved successfully! Direct staging changes are live; trigger a static website deploy to publish to vivaak.com.");
        } catch (err) {
            console.error("Error saving font config:", err);
            toast.error("Failed to save typography settings");
        } finally {
            setIsSavingFonts(false);
        }
    };

    const handleSaveGithub = async (e) => {
        e.preventDefault();
        setIsSavingGithub(true);
        try {
            await set(ref(database, "config/github"), githubConfig);
            toast.success("GitHub deployment configuration saved successfully");
            fetchHistory(); // Refresh history with new credentials
        } catch (err) {
            console.error("Error saving github config:", err);
            toast.error("Failed to update GitHub configuration");
        } finally {
            setIsSavingGithub(false);
        }
    };

    const handleTriggerDeploy = async () => {
        if (!githubConfig.token) {
            Swal.fire({
                title: "Missing GitHub Token",
                text: "Please configure and save your GitHub Personal Access Token (PAT) first in the Deployment Settings section.",
                icon: "warning",
                confirmButtonColor: "#3085d6",
            });
            return;
        }

        const result = await Swal.fire({
            title: "Publish to Production Live? 🚀",
            text: "This triggers the static build on GitHub Actions. Tell us briefly what changes you made (optional):",
            input: "text",
            inputPlaceholder: "e.g., Updated winter prices, changed hero banner...",
            showCancelButton: true,
            confirmButtonColor: "#1f2937",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, Deploy Live! 🚀",
            cancelButtonText: "Cancel",
            showLoaderOnConfirm: true,
            preConfirm: async (note) => {
                try {
                    const response = await fetch(
                        `https://api.github.com/repos/${githubConfig.owner}/${githubConfig.repo}/dispatches`,
                        {
                            method: "POST",
                            headers: {
                                Authorization: `Bearer ${githubConfig.token}`,
                                Accept: "application/vnd.github.v3+json",
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                                event_type: "deploy-production",
                            }),
                        }
                    );

                    if (response.status === 204) {
                        // Log the deployment trigger to Firebase RTDB
                        const logRef = ref(database, `config/deployLogs/${Date.now()}`);
                        await set(logRef, {
                            timestamp: Date.now(),
                            triggeredAt: new Date().toISOString(),
                            triggeredBy: user?.email || "Super Admin",
                            note: note || "No description provided",
                            status: "Triggered Successfully"
                        });
                        return true;
                    } else {
                        const errorText = await response.text();
                        throw new Error(errorText || `Status code ${response.status}`);
                    }
                } catch (error) {
                    Swal.showValidationMessage(`Trigger failed: ${error.message}`);
                    return false;
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });

        if (result.isConfirmed) {
            Swal.fire({
                title: "Build Triggered! 🚀",
                text: "The static production build and deployment pipeline has been successfully started on GitHub Actions. vivaak.com will be updated with your latest changes in a few minutes.",
                icon: "success",
                confirmButtonColor: "#10B981",
            });
            // Fetch updated history after a brief delay to allow GitHub to register the dispatch run
            setTimeout(() => {
                fetchHistory();
            }, 3000);
        }
    };

    const handleLogout = async () => {
        const result = await Swal.fire({
            title: "Logout?",
            text: "Are you sure you want to log out of the admin portal?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, logout",
        });

        if (result.isConfirmed) {
            try {
                await signOut(auth);
                toast.success("Logged out successfully");
            } catch (err) {
                console.error("Logout error:", err);
                toast.error("Failed to log out");
            }
        }
    };

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-gray-200 pb-2">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">Settings</h2>
                    <p className="text-gray-500 text-xs mt-0.5">Manage administrative configurations, security credentials, and global styling tokens.</p>
                </div>
                <div className="flex bg-gray-100 p-0.5 rounded-lg border border-gray-200">
                    <button
                        onClick={() => setActiveMainTab("general")}
                        className={`px-4 py-2 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                            activeMainTab === "general"
                                ? "bg-white text-gray-800 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        ⚙️ General Settings
                    </button>
                    <button
                        onClick={() => setActiveMainTab("deploy")}
                        className={`px-4 py-2 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                            activeMainTab === "deploy"
                                ? "bg-white text-gray-800 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        🚀 Deploy Trigger
                    </button>
                    <button
                        onClick={() => setActiveMainTab("fonts")}
                        className={`px-4 py-2 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                            activeMainTab === "fonts"
                                ? "bg-white text-gray-800 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        ✒️ Font Configuration
                    </button>
                </div>
            </div>

            {activeMainTab === "general" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-fadeIn">
                    {/* Admin Profile */}
                        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 h-fit">
                            <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-6 flex items-center gap-6">
                                <div className="h-16 w-16 rounded-full bg-white flex items-center justify-center text-2xl font-bold text-gray-800 border-4 border-gray-600 shadow-inner">
                                    {user?.email?.charAt(0).toUpperCase() || "A"}
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-white">Administrator</h3>
                                    <p className="text-gray-300 text-sm">{user?.email}</p>
                                </div>
                            </div>

                            <div className="p-6 space-y-6">
                                <div className="space-y-4">
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Email Address</label>
                                        <p className="text-gray-800 font-medium">{user?.email}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Account Role</label>
                                        <p className="text-gray-800 font-medium">Super Admin</p>
                                    </div>
                                </div>

                                <div className="pt-6 border-t border-gray-100">
                                    <button
                                        onClick={handleLogout}
                                        className="w-full px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
                                    >
                                        Logout
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* System Configuration (reCAPTCHA) */}
                        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
                            <div className="bg-gray-50 border-b border-gray-100 px-6 py-4">
                                <h3 className="font-bold text-gray-800">reCAPTCHA Configuration</h3>
                            </div>
                            <form onSubmit={handleSaveCaptcha} className="p-6 space-y-6">
                                <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gray-700 border-b pb-2">Google reCAPTCHA v2</h4>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Site Key</label>
                                        <input
                                            type="text"
                                            value={captchaConfig.siteKey}
                                            onChange={(e) => setCaptchaConfig({ ...captchaConfig, siteKey: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="Enter reCAPTCHA Site Key"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Secret Key</label>
                                        <input
                                            type="password"
                                            value={captchaConfig.secretKey}
                                            onChange={(e) => setCaptchaConfig({ ...captchaConfig, secretKey: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="Enter reCAPTCHA Secret Key"
                                        />
                                    </div>
                                </div>

                                <div className="pt-4">
                                    <button
                                        type="submit"
                                        disabled={isSaving}
                                        className="w-full px-6 py-2.5 bg-gray-800 hover:bg-gray-900 text-white font-semibold rounded-lg shadow-md transition-all disabled:bg-gray-400 cursor-pointer"
                                    >
                                        {isSaving ? "Saving..." : "Save reCAPTCHA"}
                                    </button>
                                </div>
                            </form>
                        </div>
                </div>
            )}

            {activeMainTab === "deploy" && (
                <div className="space-y-8 animate-fadeIn">
                        {/* Trigger Deploy Widget */}
                        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
                            <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-6 text-white">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h3 className="text-xl font-bold">Deploy Static Website</h3>
                                        <p className="text-gray-300 text-sm mt-1">Publish all staging changes live to vivaak.com</p>
                                    </div>
                                    <div className="text-3xl">🚀</div>
                                 </div>
                            </div>

                            <div className="p-6 space-y-6">
                                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4 text-emerald-800 text-sm leading-relaxed">
                                    <p className="font-semibold mb-1">How it works:</p>
                                    This will trigger the static production build on GitHub Actions. It pulls all the latest images, descriptions, and pages you've saved here in the admin panel, compiles them into a pre-rendered static site, and uploads it straight to <strong>vivaak.com</strong>.
                                </div>

                                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                                    <button
                                        onClick={handleTriggerDeploy}
                                        className="flex-1 px-6 py-4 bg-gray-800 hover:bg-gray-900 text-white font-bold rounded-xl shadow-lg hover:shadow-gray-800/10 hover:shadow-xl transition-all flex items-center justify-center gap-3 cursor-pointer text-lg"
                                    >
                                        <span>🚀</span> Publish to Production Live
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Deployment History Widget */}
                        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
                            <div className="bg-gray-50 border-b border-gray-100 px-6 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h3 className="font-bold text-gray-800">Deployment History & Logs</h3>
                                    <p className="text-xs text-gray-400 mt-0.5">Track production builds and admin publishing actions</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="bg-gray-100 p-0.5 rounded-lg flex border border-gray-200">
                                        <button
                                            onClick={() => setActiveHistoryTab("audit")}
                                            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${activeHistoryTab === "audit" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                                        >
                                            👤 Admin Activity Logs
                                        </button>
                                        <button
                                            onClick={() => setActiveHistoryTab("actions")}
                                            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${activeHistoryTab === "actions" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                                        >
                                            🤖 GitHub Action Status
                                        </button>
                                    </div>
                                    {activeHistoryTab === "actions" && (
                                        <button
                                            onClick={fetchHistory}
                                            disabled={isLoadingHistory}
                                            className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all disabled:opacity-50 cursor-pointer"
                                        >
                                            {isLoadingHistory ? "Refreshing..." : "🔄 Refresh"}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="p-6">
                                {activeHistoryTab === "audit" ? (
                                    localLogs.length === 0 ? (
                                        <p className="text-sm text-gray-500 text-center py-6">No admin deployment history found. Trigger your first deploy above!</p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left border-collapse">
                                                <thead>
                                                    <tr className="border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider">
                                                        <th className="pb-3 pl-2">Triggered At</th>
                                                        <th className="pb-3">Triggered By</th>
                                                        <th className="pb-3">Deployment Note / Changelog</th>
                                                        <th className="pb-3 pr-2 text-right">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-50 text-sm">
                                                    {localLogs.map((log) => {
                                                        const date = new Date(log.timestamp);
                                                        const formattedDate = date.toLocaleDateString("en-US", {
                                                            day: "numeric",
                                                            month: "short",
                                                            year: "numeric",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                            hour12: true
                                                        });

                                                        return (
                                                            <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                                                                <td className="py-4 pl-2 font-medium text-gray-600 text-xs whitespace-nowrap">
                                                                    {formattedDate}
                                                                </td>
                                                                <td className="py-4 font-semibold text-gray-800 whitespace-nowrap">
                                                                    {log.triggeredBy}
                                                                </td>
                                                                <td className="py-4 text-gray-600 italic max-w-xs truncate" title={log.note}>
                                                                    "{log.note}"
                                                                </td>
                                                                <td className="py-4 pr-2 text-right whitespace-nowrap">
                                                                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                                                                        {log.status || "Triggered Successfully"}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )
                                ) : (
                                    isLoadingHistory && history.length === 0 ? (
                                        <div className="flex justify-center items-center py-8">
                                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-800"></div>
                                        </div>
                                    ) : historyError ? (
                                        <div className="text-center py-6 px-4">
                                            <p className="text-sm text-amber-600 font-semibold mb-1">⚠️ GitHub API Restricted (403)</p>
                                            <p className="text-xs text-gray-500 max-w-md mx-auto">{historyError}</p>
                                        </div>
                                    ) : history.length === 0 ? (
                                        <p className="text-sm text-gray-500 text-center py-6">No deployment history found on GitHub. Check your configuration!</p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left border-collapse">
                                                <thead>
                                                    <tr className="border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider">
                                                        <th className="pb-3 pl-2">Run</th>
                                                        <th className="pb-3">Trigger Event</th>
                                                        <th className="pb-3">Triggered At</th>
                                                        <th className="pb-3">Status</th>
                                                        <th className="pb-3 pr-2 text-right">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-50 text-sm">
                                                    {history.map((run) => {
                                                        let statusColor = "bg-gray-100 text-gray-800";
                                                        let statusLabel = run.status;
                                                        
                                                        if (run.status === "completed") {
                                                            if (run.conclusion === "success") {
                                                                statusColor = "bg-emerald-100 text-emerald-800";
                                                                statusLabel = "Success";
                                                            } else if (run.conclusion === "failure") {
                                                                statusColor = "bg-red-100 text-red-800";
                                                                statusLabel = "Failed";
                                                            } else if (run.conclusion === "cancelled") {
                                                                statusColor = "bg-gray-100 text-gray-600";
                                                                statusLabel = "Cancelled";
                                                            } else {
                                                                statusColor = "bg-gray-100 text-gray-800";
                                                                statusLabel = run.conclusion || "Completed";
                                                            }
                                                        } else if (run.status === "in_progress") {
                                                            statusColor = "bg-amber-100 text-amber-800 animate-pulse";
                                                            statusLabel = "In Progress";
                                                        } else if (run.status === "queued") {
                                                            statusColor = "bg-blue-100 text-blue-800 animate-pulse";
                                                            statusLabel = "Queued";
                                                        }

                                                        // Format Date
                                                        const date = new Date(run.created_at);
                                                        const formattedDate = date.toLocaleDateString("en-US", {
                                                            day: "numeric",
                                                            month: "short",
                                                            year: "numeric",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                            hour12: true
                                                        });

                                                        return (
                                                            <tr key={run.id} className="hover:bg-gray-50 transition-colors">
                                                                <td className="py-4 pl-2 font-semibold text-gray-700">
                                                                    #{run.run_number}
                                                                </td>
                                                                <td className="py-4">
                                                                    <div className="flex flex-col">
                                                                        <span className="font-medium text-gray-800">
                                                                            {run.event === "repository_dispatch" ? "Repository Dispatch" : run.event === "workflow_dispatch" ? "Manual Trigger" : run.event}
                                                                        </span>
                                                                        <span className="text-xs text-gray-400">
                                                                            {run.display_title || run.head_commit?.message || "Triggered via Admin Panel API"}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="py-4 text-gray-600 text-xs">
                                                                    {formattedDate}
                                                                </td>
                                                                <td className="py-4">
                                                                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColor}`}>
                                                                        {statusLabel}
                                                                    </span>
                                                                </td>
                                                                <td className="py-4 pr-2 text-right">
                                                                    <a
                                                                        href={run.html_url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded text-xs font-semibold transition-all inline-block cursor-pointer"
                                                                    >
                                                                        Open Details ↗
                                                                    </a>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>

                        {/* GitHub Configuration Form */}
                        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
                            <div className="bg-gray-50 border-b border-gray-100 px-6 py-4">
                                <h3 className="font-bold text-gray-800">Deployment Settings (GitHub Configuration)</h3>
                            </div>
                            <form onSubmit={handleSaveGithub} className="p-6 space-y-6">
                                <p className="text-xs text-gray-500">
                                    Configure the GitHub Personal Access Token (PAT) with `repo` or `workflow` scope to allow the admin panel to trigger your static build actions.
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Repository Owner</label>
                                        <input
                                            type="text"
                                            value={githubConfig.owner}
                                            onChange={(e) => setGithubConfig({ ...githubConfig, owner: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="Repository Owner (e.g. niteshsrivastava7959)"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Repository Name</label>
                                        <input
                                            type="text"
                                            value={githubConfig.repo}
                                            onChange={(e) => setGithubConfig({ ...githubConfig, repo: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="Repository Name (e.g. vivaak-frontend)"
                                            required
                                        />
                                    </div>
                                    <div className="md:col-span-2 space-y-2">
                                        <label className="text-xs font-bold text-gray-500 uppercase">GitHub Personal Access Token (PAT)</label>
                                        <input
                                            type="password"
                                            value={githubConfig.token}
                                            onChange={(e) => setGithubConfig({ ...githubConfig, token: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-gray-50 flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={isSavingGithub}
                                        className="px-6 py-2.5 bg-gray-800 hover:bg-gray-900 text-white font-semibold rounded-lg shadow-md transition-all disabled:bg-gray-400 cursor-pointer"
                                    >
                                        {isSavingGithub ? "Saving Settings..." : "Save Settings"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
            )}

            {activeMainTab === "fonts" && (
                <form onSubmit={handleSaveFonts} className="space-y-8 animate-fadeIn">
                    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                        <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-6 text-white">
                            <h3 className="text-xl font-bold">Global Typography Settings</h3>
                            <p className="text-gray-300 text-sm mt-1">Configure font families, weights, spacing, and casing rules for your frontend site.</p>
                        </div>
                        
                        <div className="p-6 space-y-8 divide-y divide-gray-100">
                            {/* Heading Typography */}
                            <div className="pt-0 space-y-4">
                                <h4 className="text-md font-bold text-gray-800 flex items-center gap-2">
                                    <span className="text-gray-400">01.</span> Headings & Destine Titles
                                </h4>
                                <p className="text-xs text-gray-400">Controls h1 to h6, villa cards, and core destination highlights.</p>
                                
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Font Family</label>
                                        <select
                                            value={fontConfig.heading.family}
                                            onChange={(e) => setFontConfig({
                                                ...fontConfig,
                                                heading: { ...fontConfig.heading, family: e.target.value }
                                            })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                                        >
                                            <option value="Cinzel">Cinzel (Local Classic)</option>
                                            <option value="Trajan">Trajan Pro (Premium Majestic)</option>
                                            <option value="Outfit">Outfit (Clean Sans-Serif)</option>
                                            <option value="Playfair Display">Playfair Display (Google Font)</option>
                                            <option value="Lora">Lora (Google Font)</option>
                                            <option value="Inter">Inter (Google Font)</option>
                                            <option value="Montserrat">Montserrat (Modern Sans-Serif)</option>
                                            <option value="Cormorant Garamond">Cormorant Garamond (Luxury Serif)</option>
                                            <option value="Italiana">Italiana (High-End Elegant Serif)</option>
                                            <option value="Marcellus">Marcellus (Clean Roman Flare Serif)</option>
                                            <option value="Jost">Jost (Geometric Modern Sans)</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Font Weight</label>
                                        <select
                                            value={fontConfig.heading.weight}
                                            onChange={(e) => setFontConfig({
                                                ...fontConfig,
                                                heading: { ...fontConfig.heading, weight: e.target.value }
                                            })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                                        >
                                            <option value="300">300 - Light</option>
                                            <option value="400">400 - Regular</option>
                                            <option value="500">500 - Medium</option>
                                            <option value="600">600 - SemiBold</option>
                                            <option value="700">700 - Bold</option>
                                            <option value="800">800 - ExtraBold</option>
                                            <option value="900">900 - Black</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Letter Spacing</label>
                                        <input
                                            type="text"
                                            value={fontConfig.heading.spacing}
                                            onChange={(e) => setFontConfig({
                                                ...fontConfig,
                                                heading: { ...fontConfig.heading, spacing: e.target.value }
                                            })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                                            placeholder="e.g. -1px, 0.05em"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Text Transform (Casing)</label>
                                        <select
                                            value={fontConfig.heading.transform}
                                            onChange={(e) => setFontConfig({
                                                ...fontConfig,
                                                heading: { ...fontConfig.heading, transform: e.target.value }
                                            })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                                        >
                                            <option value="uppercase">UPPERCASE (ALL CAPS)</option>
                                            <option value="lowercase">lowercase</option>
                                            <option value="capitalize">Capitalize Words</option>
                                            <option value="none">none (Default case)</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Font Style</label>
                                        <select
                                            value={fontConfig.heading.style}
                                            onChange={(e) => setFontConfig({
                                                ...fontConfig,
                                                heading: { ...fontConfig.heading, style: e.target.value }
                                            })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                                        >
                                            <option value="normal">Normal</option>
                                            <option value="italic">Italic</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Menu/Navigation Typography */}
                            <div className="pt-6 space-y-4">
                                <h4 className="text-md font-bold text-gray-800 flex items-center gap-2">
                                    <span className="text-gray-400">02.</span> Menu & Navigation Text
                                </h4>
                                <p className="text-xs text-gray-400">Controls the header menu links and dropdown selections.</p>
                                
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Font Family</label>
                                        <select
                                            value={fontConfig.menu.family}
                                            onChange={(e) => setFontConfig({
                                                ...fontConfig,
                                                menu: { ...fontConfig.menu, family: e.target.value }
                                            })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                                        >
                                            <option value="Cinzel">Cinzel (Local Classic)</option>
                                            <option value="Trajan">Trajan Pro (Premium Majestic)</option>
                                            <option value="Outfit">Outfit (Clean Sans-Serif)</option>
                                            <option value="Playfair Display">Playfair Display (Google Font)</option>
                                            <option value="Lora">Lora (Google Font)</option>
                                            <option value="Inter">Inter (Google Font)</option>
                                            <option value="Montserrat">Montserrat (Modern Sans-Serif)</option>
                                            <option value="Cormorant Garamond">Cormorant Garamond (Luxury Serif)</option>
                                            <option value="Italiana">Italiana (High-End Elegant Serif)</option>
                                            <option value="Marcellus">Marcellus (Clean Roman Flare Serif)</option>
                                            <option value="Jost">Jost (Geometric Modern Sans)</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Font Weight</label>
                                        <select
                                            value={fontConfig.menu.weight}
                                            onChange={(e) => setFontConfig({
                                                ...fontConfig,
                                                menu: { ...fontConfig.menu, weight: e.target.value }
                                            })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                                        >
                                            <option value="300">300 - Light</option>
                                            <option value="400">400 - Regular</option>
                                            <option value="500">500 - Medium</option>
                                            <option value="600">600 - SemiBold</option>
                                            <option value="700">700 - Bold</option>
                                            <option value="800">800 - ExtraBold</option>
                                            <option value="900">900 - Black</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Letter Spacing</label>
                                        <input
                                            type="text"
                                            value={fontConfig.menu.spacing}
                                            onChange={(e) => setFontConfig({
                                                ...fontConfig,
                                                menu: { ...fontConfig.menu, spacing: e.target.value }
                                            })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                                            placeholder="e.g. 0.05em, 1px"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Text Transform (Casing)</label>
                                        <select
                                            value={fontConfig.menu.transform}
                                            onChange={(e) => setFontConfig({
                                                ...fontConfig,
                                                menu: { ...fontConfig.menu, transform: e.target.value }
                                            })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                                        >
                                            <option value="uppercase">UPPERCASE (ALL CAPS)</option>
                                            <option value="lowercase">lowercase</option>
                                            <option value="capitalize">Capitalize Words</option>
                                            <option value="none">none (Default case)</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Font Style</label>
                                        <select
                                            value={fontConfig.menu.style}
                                            onChange={(e) => setFontConfig({
                                                ...fontConfig,
                                                menu: { ...fontConfig.menu, style: e.target.value }
                                            })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                                        >
                                            <option value="normal">Normal</option>
                                            <option value="italic">Italic</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Body Typography */}
                            <div className="pt-6 space-y-4">
                                <h4 className="text-md font-bold text-gray-800 flex items-center gap-2">
                                    <span className="text-gray-400">03.</span> Body & General Descriptions
                                </h4>
                                <p className="text-xs text-gray-400">Controls paragraphs, labels, list items, and standard site texts.</p>
                                
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Font Family</label>
                                        <select
                                            value={fontConfig.body.family}
                                            onChange={(e) => setFontConfig({
                                                ...fontConfig,
                                                body: { ...fontConfig.body, family: e.target.value }
                                            })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                                        >
                                            <option value="Outfit">Outfit (Clean Sans-Serif)</option>
                                            <option value="Cinzel">Cinzel (Local Classic)</option>
                                            <option value="Trajan">Trajan Pro (Premium Majestic)</option>
                                            <option value="Playfair Display">Playfair Display (Google Font)</option>
                                            <option value="Lora">Lora (Google Font)</option>
                                            <option value="Inter">Inter (Google Font)</option>
                                            <option value="Montserrat">Montserrat (Modern Sans-Serif)</option>
                                            <option value="Cormorant Garamond">Cormorant Garamond (Luxury Serif)</option>
                                            <option value="Italiana">Italiana (High-End Elegant Serif)</option>
                                            <option value="Marcellus">Marcellus (Clean Roman Flare Serif)</option>
                                            <option value="Jost">Jost (Geometric Modern Sans)</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Font Weight</label>
                                        <select
                                            value={fontConfig.body.weight}
                                            onChange={(e) => setFontConfig({
                                                ...fontConfig,
                                                body: { ...fontConfig.body, weight: e.target.value }
                                            })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                                        >
                                            <option value="300">300 - Light</option>
                                            <option value="400">400 - Regular</option>
                                            <option value="500">500 - Medium</option>
                                            <option value="600">600 - SemiBold</option>
                                            <option value="700">700 - Bold</option>
                                            <option value="800">800 - ExtraBold</option>
                                            <option value="900">900 - Black</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Letter Spacing</label>
                                        <input
                                            type="text"
                                            value={fontConfig.body.spacing}
                                            onChange={(e) => setFontConfig({
                                                ...fontConfig,
                                                body: { ...fontConfig.body, spacing: e.target.value }
                                            })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                                            placeholder="e.g. normal, 0.5px"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Text Transform (Casing)</label>
                                        <select
                                            value={fontConfig.body.transform}
                                            onChange={(e) => setFontConfig({
                                                ...fontConfig,
                                                body: { ...fontConfig.body, transform: e.target.value }
                                            })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                                        >
                                            <option value="none">none (Default case)</option>
                                            <option value="uppercase">UPPERCASE (ALL CAPS)</option>
                                            <option value="lowercase">lowercase</option>
                                            <option value="capitalize">Capitalize Words</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 uppercase">Font Style</label>
                                        <select
                                            value={fontConfig.body.style}
                                            onChange={(e) => setFontConfig({
                                                ...fontConfig,
                                                body: { ...fontConfig.body, style: e.target.value }
                                            })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
                                        >
                                            <option value="normal">Normal</option>
                                            <option value="italic">Italic</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-gray-100">
                            <p className="text-xs text-gray-500">
                                💡 Tip: Dynamic overrides are immediately injected into your staging environment! Trigger a live deploy to update the production site.
                            </p>
                            <button
                                type="submit"
                                disabled={isSavingFonts}
                                className="px-6 py-2.5 bg-gray-800 hover:bg-gray-900 text-white font-semibold rounded-lg shadow-md transition-all disabled:bg-gray-400 cursor-pointer flex items-center gap-2"
                            >
                                {isSavingFonts ? "Saving Typography Settings..." : "✒️ Save Typography Settings"}
                            </button>
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
};

export default Settings;
